import React, { useState } from 'react';
import { BookOpen, ExternalLink, Search, Filter } from 'lucide-react';

const Resources = () => {
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedCategory, setSelectedCategory] = useState('all');

  const resourceCategories = [
    { id: 'all', name: 'All Resources' },
    { id: 'fundamentals', name: 'Fundamentals' },
    { id: 'siem', name: 'SIEM & Monitoring' },
    { id: 'incident-response', name: 'Incident Response' },
    { id: 'threat-hunting', name: 'Threat Hunting' },
    { id: 'forensics', name: 'Digital Forensics' },
    { id: 'tools', name: 'Tools & Labs' },
  ];

  const resources = [
    {
      category: 'fundamentals',
      title: 'Introduction to Cybersecurity',
      description: 'Learn the basics of cybersecurity, including the CIA triad, security principles, and common threats.',
      url: 'https://www.cybrary.it/course/introduction-to-cybersecurity/',
      type: 'Course',
      difficulty: 'Beginner',
    },
    {
      category: 'siem',
      title: 'SOC Analyst Training',
      description: 'Complete training path for aspiring SOC analysts with hands-on labs and real-world scenarios.',
      url: 'https://blueteamsacademy.com/courses/soc-level-1-certification',
      type: 'Course',
      difficulty: 'Beginner',
    },
    {
      category: 'fundamentals',
      title: 'Networking Fundamentals',
      description: 'Master TCP/IP, OSI model, and network protocols essential for security professionals.',
      url: 'https://www.professormesser.com/network-plus/n10-008/n10-008-video/n10-008-training-course/',
      type: 'Video Course',
      difficulty: 'Beginner',
    },
    {
      category: 'fundamentals',
      title: 'Introduction to Linux',
      description: 'Essential Linux fundamentals for cybersecurity professionals - commands, file systems, and security basics.',
      url: 'https://www.edx.org/learn/linux/the-linux-foundation-introduction-to-linux',
      type: 'Course',
      difficulty: 'Beginner',
    },
    {
      category: 'fundamentals',
      title: 'Types of Malware',
      description: 'Comprehensive guide to understanding different types of malware, their behavior, and detection methods.',
      url: 'https://www.malwarebytes.com/malware',
      type: 'Guide',
      difficulty: 'Beginner',
    },
    {
      category: 'siem',
      title: 'Splunk Fundamentals',
      description: 'Learn to use Splunk for security monitoring, log analysis, and incident detection.',
      url: 'https://www.splunk.com/en_us/training/free-courses/splunk-fundamentals-1.html',
      type: 'Course',
      difficulty: 'Intermediate',
    },
    {
      category: 'siem',
      title: 'ELK Stack for Security',
      description: 'Build and configure the Elastic Stack for security monitoring and threat detection.',
      url: 'https://www.elastic.co/training/free',
      type: 'Tutorial',
      difficulty: 'Intermediate',
    },
    {
      category: 'incident-response',
      title: 'SANS Incident Response',
      description: 'Comprehensive guide to incident response methodology and best practices.',
      url: 'https://www.sans.org/white-papers/incident-handlers-handbook/',
      type: 'Guide',
      difficulty: 'Intermediate',
    },
    {
      category: 'incident-response',
      title: 'NIST Incident Response Framework',
      description: 'Official NIST guide for computer security incident handling.',
      url: 'https://nvlpubs.nist.gov/nistpubs/SpecialPublications/NIST.SP.800-61r2.pdf',
      type: 'Documentation',
      difficulty: 'Advanced',
    },
    {
      category: 'threat-hunting',
      title: 'Active Countermeasures Threat Hunting',
      description: 'Comprehensive threat hunting course with open-source tools and real-world hunting methodologies.',
      url: 'https://www.activecountermeasures.com/threat-hunting-training-course/',
      type: 'Training',
      difficulty: 'Advanced',
    },
    {
      category: 'threat-hunting',
      title: 'Cyber Threat Hunting Course',
      description: 'Learn proactive threat hunting techniques, hypothesis-driven hunting, and advanced detection strategies.',
      url: 'https://www.sans.org/cyber-security-courses/cyber-threat-hunting/',
      type: 'Course',
      difficulty: 'Advanced',
    },
    {
      category: 'threat-hunting',
      title: 'MITRE ATT&CK Framework',
      description: 'Understand adversary tactics and techniques for effective threat hunting.',
      url: 'https://attack.mitre.org/',
      type: 'Framework',
      difficulty: 'Intermediate',
    },
    {
      category: 'forensics',
      title: 'Digital Forensics Basics',
      description: 'Introduction to digital forensics, evidence collection, and analysis.',
      url: 'https://www.cybrary.it/course/digital-forensics/',
      type: 'Course',
      difficulty: 'Intermediate',
    },
    {
      category: 'forensics',
      title: 'Memory Forensics',
      description: 'Learn memory analysis using Volatility and other forensic tools.',
      url: 'https://www.memoryanalysis.net/amf',
      type: 'Course',
      difficulty: 'Advanced',
    },
    {
      category: 'tools',
      title: 'Security Onion',
      description: 'Free and open platform for threat hunting, network security monitoring, and log management.',
      url: 'https://securityonionsolutions.com/',
      type: 'Tool',
      difficulty: 'Intermediate',
    },
    {
      category: 'tools',
      title: 'TryHackMe Blue Team Path',
      description: 'Hands-on labs and challenges for practicing defensive security skills.',
      url: 'https://tryhackme.com/path/outline/blueteam',
      type: 'Lab Platform',
      difficulty: 'Beginner',
    },
    {
      category: 'tools',
      title: 'CyberDefenders',
      description: 'Blue team CTF challenges focusing on incident response and threat hunting.',
      url: 'https://cyberdefenders.org/',
      type: 'CTF Platform',
      difficulty: 'Intermediate',
    },
    {
      category: 'tools',
      title: 'LetsDefend',
      description: 'SOC analyst training platform with real-world scenarios and alerts.',
      url: 'https://letsdefend.io/',
      type: 'Training Platform',
      difficulty: 'Beginner',
    },
  ];

  const filteredResources = resources.filter((resource) => {
    const matchesCategory = selectedCategory === 'all' || resource.category === selectedCategory;
    const matchesSearch = resource.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
                          resource.description.toLowerCase().includes(searchTerm.toLowerCase());
    return matchesCategory && matchesSearch;
  });

  const getDifficultyColor = (difficulty) => {
    switch (difficulty) {
      case 'Beginner':
        return 'text-green-400 bg-green-400/10';
      case 'Intermediate':
        return 'text-yellow-400 bg-yellow-400/10';
      case 'Advanced':
        return 'text-red-400 bg-red-400/10';
      default:
        return 'text-gray-400 bg-gray-400/10';
    }
  };

  return (
    <div className="min-h-screen pt-24 pb-20">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="text-center mb-16">
          <h1 className="text-4xl md:text-6xl font-bold mb-6 font-mono">
            <span className="text-accent">&gt;</span> Learning Resources
          </h1>
          <p className="text-gray-400 text-lg max-w-3xl mx-auto">
            Curated resources to help you master defensive cybersecurity. From fundamentals to advanced techniques.
          </p>
        </div>

        {/* Search and Filter */}
        <div className="mb-12">
          <div className="flex flex-col md:flex-row gap-4 mb-6">
            {/* Search */}
            <div className="flex-1 relative">
              <Search className="absolute left-4 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
              <input
                type="text"
                placeholder="Search resources..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full bg-secondary border border-accent/20 rounded-lg py-3 pl-12 pr-4 text-white placeholder-gray-500 focus:outline-none focus:border-accent transition-colors font-mono"
              />
            </div>
          </div>

          {/* Category Filter */}
          <div className="flex items-center gap-3 overflow-x-auto pb-2">
            <Filter className="text-accent w-5 h-5 flex-shrink-0" />
            {resourceCategories.map((category) => (
              <button
                key={category.id}
                onClick={() => setSelectedCategory(category.id)}
                className={`px-4 py-2 rounded-lg font-mono text-sm whitespace-nowrap transition-all ${
                  selectedCategory === category.id
                    ? 'bg-accent text-primary'
                    : 'bg-secondary text-gray-400 hover:bg-accent/20 hover:text-accent border border-accent/20'
                }`}
              >
                {category.name}
              </button>
            ))}
          </div>
        </div>

        {/* Resources Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {filteredResources.map((resource, index) => (
            <div
              key={index}
              className="bg-secondary border border-accent/20 rounded-lg p-6 hover:border-accent transition-all duration-300 transform hover:-translate-y-2 hover:shadow-xl hover:shadow-accent/10"
            >
              <div className="flex items-start justify-between mb-4">
                <div className="bg-accent/10 p-3 rounded-lg">
                  <BookOpen className="w-6 h-6 text-accent" />
                </div>
                <div className="flex gap-2">
                  <span className={`text-xs px-2 py-1 rounded-full font-mono ${getDifficultyColor(resource.difficulty)}`}>
                    {resource.difficulty}
                  </span>
                </div>
              </div>

              <h3 className="text-xl font-bold text-white mb-3 font-mono group-hover:text-accent transition-colors">
                {resource.title}
              </h3>

              <p className="text-gray-400 text-sm mb-4 line-clamp-3">
                {resource.description}
              </p>

              <div className="flex items-center justify-between">
                <span className="text-xs text-accent-blue font-mono">{resource.type}</span>
                <a
                  href={resource.url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="group/link inline-flex items-center space-x-2 text-accent hover:text-accent-blue transition-colors"
                >
                  <span className="text-sm font-mono">Access</span>
                  <ExternalLink className="w-4 h-4 group-hover/link:translate-x-1 transition-transform" />
                </a>
              </div>
            </div>
          ))}
        </div>

        {/* No Results */}
        {filteredResources.length === 0 && (
          <div className="text-center py-20">
            <p className="text-gray-400 text-lg font-mono">No resources found matching your criteria.</p>
          </div>
        )}

        {/* Call to Action */}
        <div className="mt-20 bg-gradient-to-r from-accent/10 to-accent-blue/10 border border-accent/20 rounded-lg p-8 md:p-12 text-center">
          <h3 className="text-2xl md:text-3xl font-bold text-white mb-4 font-mono">
            Have a resource to share?
          </h3>
          <p className="text-gray-400 mb-6 max-w-2xl mx-auto">
            Help the community grow by suggesting valuable learning resources. Reach out to us on Discord!
          </p>
          <a
            href="https://discord.com/invite/EYmWz5Cu"
            target="_blank"
            rel="noopener noreferrer"
            className="inline-block bg-accent hover:bg-accent-blue text-primary font-bold py-3 px-8 rounded-lg font-mono transition-all duration-300 transform hover:scale-105"
          >
            Contribute Resources
          </a>
        </div>
      </div>
    </div>
  );
};

export default Resources;
